// Tabblad "Dekking" van het vlak Bronnen & dekking (W6 ronde 3, besluit D2).
//
// Waar zitten de gaten? Vijf meters met hun doel, het raster categorie ×
// belang, en per gat één actie die de beheerder als verzoek op de wachtrij zet
// (POST /api/beheer/dekking/verzoek — daarna beslist hij er op de wachtrij
// ja of nee op, met "bestaat al" en de raming erbij).
//
// De stand van elke meter beslist de server (D2: rood onder de helft van het
// doel). Dit tabblad zet hem alleen neer. Een meter zonder doel of zonder
// meting draagt een reden en een grijs stoplicht — nooit stilletjes groen
// omdat het getal hoog oogt.

import { useState } from "react";

import { Card, InfoIcon, ReasonBadge, TableHead } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { ApiError, api } from "../../lib/api";
import type { DekkingMeter, DekkingResponse, Raming } from "../../contracts";
import { color, sectionLabel, size, weight } from "../../tokens";
import { DollarIcoon, VinkIcoon, WachtrijIcoon } from "./Iconen";
import { Stoplicht } from "./Stoplicht";
import { knopLicht } from "./stijl";
import { Blok, aantal, cijfer, getal, noot, procent } from "./BronnenStukken";
import { leesbaar } from "./woorden";

type RasterRij = DekkingResponse["raster"][number];

const RASTER_KOLOMMEN = "minmax(260px,3fr) 110px 110px";

export function BronnenDekking({ data, sector }: { data: DekkingResponse; sector: string }) {
  const gaten = data.raster.filter((r) => r.gat).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <Blok
        titel="Meters"
        rechts={
          // Alleen "concept" is een woord voor de beheerder; een andere
          // status is een interne code en blijft van het scherm.
          data.status === "concept" ? (
            <ReasonBadge
              text="doelen: concept"
              tone="amber"
              title="De doelen zijn nog een voorstel — de owner stelt ze vast."
            />
          ) : undefined
        }
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
            gap: 12,
          }}
        >
          {data.meters.map((m) => (
            <MeterKaart key={m.sleutel} meter={m} />
          ))}
        </div>
        {data.meters.length === 0 && <EmptyReason reason="de server gaf geen meters terug" />}
      </Blok>

      <Blok titel="Categorie × belang">
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {data.raster.length === 0 ? (
            <div style={{ padding: 18 }}>
              <EmptyReason reason="deze sector heeft geen vaste categorieën om langs te lopen" />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 520 }}>
                <TableHead
                  cols={RASTER_KOLOMMEN}
                  labels={["Categorie", "Dragend", "Achtergrond"]}
                />
                {data.raster.map((r, i) => (
                  <RasterRegel key={r.categorie} rij={r} eerste={i === 0} sector={sector} />
                ))}
              </div>
            </div>
          )}
        </Card>
        <p style={noot}>
          {gaten === 0
            ? "Geen gaten in het raster."
            : `${aantal(gaten, "gat", "gaten")} — bij elk gat staat een actie.`}{" "}
          Dragend: trends die de categorie zelf dragen · achtergrond: trends die haar alleen
          onderbouwen.
        </p>
      </Blok>

      <Blok titel="Categorieën buiten de vaste lijst">
        <Card style={{ padding: data.buitenDeLijst.length ? 0 : 18 }}>
          {data.buitenDeLijst.length === 0 ? (
            <EmptyReason reason="geen trend valt buiten de vaste categorieën" />
          ) : (
            <ul style={{ listStyle: "none" }}>
              {data.buitenDeLijst.map((b, i) => (
                <li
                  key={b.categorie}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "8px 16px",
                    borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                    fontSize: size.meta,
                    color: color.body,
                  }}
                >
                  <span>{b.categorie}</span>
                  <span style={{ ...cijfer, color: color.faint, whiteSpace: "nowrap" }}>
                    {aantal(b.trends, "trend", "trends")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </Blok>
    </div>
  );
}

function MeterKaart({ meter: m }: { meter: DekkingMeter }) {
  // Aandeel en doel alleen als de server ze stuurde; geen van beide rekenen
  // we zelf uit de waarde-zin.
  const stukken: string[] = [];
  if (m.aandeel !== null) stukken.push(procent(m.aandeel));
  if (m.doel !== null) stukken.push(`doel ${procent(m.doel)}`);

  return (
    <Card style={{ padding: "16px 18px" }}>
      <div data-meter={m.sleutel} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...sectionLabel, marginBottom: 0, flex: 1, minWidth: 0 }}>{m.label}</span>
          {m.concept && (
            <ReasonBadge
              text="concept"
              tone="amber"
              title="Dit doel is een voorstel — de owner stelt het vast."
            />
          )}
          {/* Geen i'tje met een lege tooltip: zonder uitleg geen icoon. */}
          {m.uitleg.trim() && <InfoIcon tooltip={m.uitleg} />}
          {/* Een meter zonder doel is wél gemeten (25 van 25): het stoplicht zegt
              dan de reden van de server, niet "niet gemeten". */}
          <Stoplicht stand={m.stand} reden={m.reden} />
        </div>

        {m.waarde !== null ? (
          <span
            style={{
              fontSize: 19,
              fontWeight: weight.bold,
              color: color.ink,
              lineHeight: 1.25,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {m.waarde}
          </span>
        ) : (
          <span style={{ fontSize: size.meta, color: color.soft, fontStyle: "italic", lineHeight: 1.5 }}>
            {m.reden ?? "De server gaf geen waarde en geen reden."}
          </span>
        )}

        {stukken.length > 0 && (
          <span style={{ ...cijfer, fontSize: size.meta, color: color.body }}>
            {stukken.join(" · ")}
          </span>
        )}

        {/* Een waarde mét reden (bv. gemeten, maar zonder doel): de reden
            staat eronder, zodat het ontbrekende doel niet onzichtbaar wordt. */}
        {m.waarde !== null && m.reden && (
          <span style={{ fontSize: size.meta, color: color.soft, fontStyle: "italic", lineHeight: 1.5 }}>
            {m.reden}
          </span>
        )}

        {m.doelBron && (
          <span style={{ fontSize: 12, color: color.faint, lineHeight: 1.5 }}>{m.doelBron}</span>
        )}
      </div>
    </Card>
  );
}

type VerzoekStaat =
  | { soort: "rust" }
  | { soort: "bezig" }
  | { soort: "klaar"; zin: string }
  | { soort: "fout"; reden: string };

function RasterRegel({ rij, eerste, sector }: { rij: RasterRij; eerste: boolean; sector: string }) {
  const [staat, setStaat] = useState<VerzoekStaat>({ soort: "rust" });
  const bezig = staat.soort === "bezig";

  async function zetOpWachtrij() {
    setStaat({ soort: "bezig" });
    try {
      await api.dekkingVerzoek({ sector, categorie: rij.categorie });
      setStaat({ soort: "klaar", zin: "Staat op de wachtrij" });
    } catch (err) {
      // 409 = dit gat staat al open op de wachtrij. Geen fout van deze
      // beheerder: de zin van de server zegt het, en de knop verdwijnt.
      if (err instanceof ApiError && err.status === 409) {
        setStaat({ soort: "klaar", zin: leesbaar(err, "Deze actie staat al open op de wachtrij.") });
        return;
      }
      setStaat({ soort: "fout", reden: leesbaar(err, "Het verzoek kon niet op de wachtrij.") });
    }
  }

  return (
    <div
      data-categorie={rij.categorie}
      style={{
        display: "grid",
        gridTemplateColumns: RASTER_KOLOMMEN,
        alignItems: "center",
        padding: "10px 16px",
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        fontSize: size.meta,
        color: color.body,
        background: rij.gat ? color.accentAmberBg : undefined,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span style={{ color: color.ink }}>{rij.categorie}</span>
        {/* Het woord "gat" komt uit de vlag van de server, niet uit een
            eigen drempel op het getal. */}
        {rij.gat && <ReasonBadge text="gat" tone="amber" title="Geen dragende trend in deze categorie." />}
      </span>
      <span style={{ ...cijfer, textAlign: "center" }}>{getal(rij.drager)}</span>
      <span style={{ ...cijfer, textAlign: "center" }}>{getal(rij.context)}</span>

      {rij.gat && (
        <div
          style={{ gridColumn: "1 / -1", marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}
        >
          {rij.actie ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                <span style={{ color: color.body }}>{rij.actie.tekst}</span>
                {staat.soort === "klaar" ? (
                  <span
                    role="status"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: color.accentGreen,
                      fontWeight: weight.medium,
                    }}
                  >
                    <VinkIcoon /> {staat.zin}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void zetOpWachtrij()}
                    disabled={bezig}
                    style={{
                      ...knopLicht,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      background: color.surface,
                      color: bezig ? color.faint : color.body,
                      cursor: bezig ? "default" : "pointer",
                    }}
                  >
                    <WachtrijIcoon /> {bezig ? "bezig…" : "Als verzoek op de wachtrij"}
                  </button>
                )}
              </div>
              <RamingRegel raming={rij.actie.raming} />
              {staat.soort === "fout" && (
                <p style={{ color: color.accentRed }}>{staat.reden}</p>
              )}
            </>
          ) : (
            <EmptyReason reason="de server gaf bij dit gat geen actie" />
          )}
        </div>
      )}
    </div>
  );
}

/** De raming: altijd de tekst van de server; een bedrag alleen als hij er een
 *  heeft — nooit een verzonnen nul (zelfde afspraak als op de wachtrij). */
function RamingRegel({ raming }: { raming: Raming }) {
  return (
    <p style={{ display: "flex", alignItems: "center", gap: 6, color: color.faint, lineHeight: 1.5 }}>
      <DollarIcoon />
      <span>
        {raming.bedragUsd !== null &&
          `$${raming.bedragUsd.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} · `}
        {raming.tekst}
      </span>
    </p>
  );
}
